import { useMemo } from "react";

import { getDomainsData, processRankPositionData, sortByDomainOrder } from "../../../helpers/helpers";
import useTrafficSearchApiParams from "../../../hooks/useApiParamsSearch";
import { useRankDistributionData } from "../../../services/search-overview-api/apiHooks";
import type { SearchRankDistributionDataSchema } from "../../../services/search-overview-api/types";
import type { DomainInfo, FormattedNumber } from "../../../types";

type TransformedRankPositionData = {
    transformedData: {
        change: number;
        percentageOf: FormattedNumber;
        keywords: string;
        domain: DomainInfo;
    }[];
    country: number;
};

const transformRankPositionData = (data: SearchRankDistributionDataSchema): TransformedRankPositionData => {
    if (!data?.RankSplit) {
        return { transformedData: [], country: null };
    }

    const country = data?.Country;
    const domainsDataMap = getDomainsData();

    const transformedData = Object.entries(data.RankSplit).reduce(
        (acc, [domain, domainMetrics]) => {
            if (!domainMetrics) {
                return acc;
            }

            const res = processRankPositionData(domainMetrics);
            acc.push({
                domain: domainsDataMap[domain],
                ...res,
            });

            return acc;
        },
        [] as TransformedRankPositionData["transformedData"],
    );
    const sortedData = sortByDomainOrder(transformedData, (item) => item.domain.name);

    return { transformedData: sortedData, country };
};

const useRankPositionTable = () => {
    const { trafficSearchApiParams } = useTrafficSearchApiParams();
    const { data, isLoading, isError } = useRankDistributionData(trafficSearchApiParams);

    const { transformedData, country } = useMemo(() => transformRankPositionData(data), [data]);

    return { data: transformedData, country, isLoading, isError };
};

export default useRankPositionTable;
